import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { z } from 'zod';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { TSpace } from '../../types/space.type';

export const CreateSpaceAdminValidationSchema = z.object({
  name: z
    .string({ required_error: 'Space name is required with at least 1 character' })
    .min(1, { message: 'Space name is required with at least 1 character' }),
  description: z.string().optional(),
  icon: z.instanceof(File).optional(),
});

export type TCreateSpaceAdminValidation = z.infer<typeof CreateSpaceAdminValidationSchema>;
export type TCreateSpaceAdminPayload = TCreateSpaceAdminValidation;
export type TCreateSpaceAdminResponse = ApiResponse<TSpace>;

export const createSpaceAdmin = async (payload: TCreateSpaceAdminPayload): Promise<TCreateSpaceAdminResponse> => {
  const url = '/space/admin';

  const formData = new FormData();

  formData.append('name', payload.name);

  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.icon) {
    formData.append('icon', payload.icon);
  }

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  return axiosInstance.post(url, formData, config);
};

/**
 * Use Create Space Admin React-Query Hook
 */
type UseCreateSpaceAdminMutationOptions = {
  mutationConfig?: TypedMutationConfig<typeof createSpaceAdmin, AxiosErrorCustom>;
};

/**
 * Use Create Space Admin React-Query Hook
 */
export const useCreateSpaceAdminMutation = ({ mutationConfig }: UseCreateSpaceAdminMutationOptions) => {
  const { ...restConfig } = mutationConfig ?? {};

  return useMutation({
    mutationFn: createSpaceAdmin,
    ...restConfig,
  });
};
